/**
 * Thin fetch wrappers around the Phase D backend (`functions/api/auth/*`).
 *
 * This is the only file that knows the endpoint paths. Every function
 * distinguishes a definite server answer (success / rejected) from "the
 * network isn't reachable" (fetch itself threw) — only the former is ever
 * treated as authoritative. See AUTH-ARCHITECTURE.md §10–§12.
 *
 * `credentials: 'include'` on every call is what lets the browser attach and
 * receive the `HttpOnly` session cookie; nothing in this file ever reads,
 * stores, or forwards the cookie value itself — the browser owns it.
 */

/** Master Phase 11 — authorization, orthogonal to authentication. Never inferred client-side. */
export type UserRole = 'user' | 'admin';

export interface LoginSuccess {
  outcome: 'success';
  email: string;
}
export interface LoginRejected {
  outcome: 'invalid-credentials';
}
/** Registration + Approval plan §8 — a self-registered account not yet
 *  approved by an administrator. Distinct from `invalid-credentials` so the
 *  login screen can show its own specific message rather than the generic
 *  "incorrect email or password" (login.ts's own deliberate exception —
 *  see that file's comment on `pendingApproval()`). */
export interface LoginPendingApproval {
  outcome: 'pending-approval';
}
/** Email-verification addendum — correct credentials, but the account has
 *  never proven control of its mailbox. Distinct from `pending-approval`
 *  because the user's next action is different (check your inbox / resend,
 *  rather than wait for an administrator). Both arrive as a 403; the
 *  server's `reason` field is what separates them, never the error text. */
export interface LoginEmailUnverified {
  outcome: 'email-unverified';
}
export interface LoginNetworkError {
  outcome: 'network-error';
}
export type LoginResult =
  | LoginSuccess
  | LoginRejected
  | LoginPendingApproval
  | LoginEmailUnverified
  | LoginNetworkError;

export type LogoutOutcome = 'revoked' | 'network-error';

export interface MeAuthenticated {
  outcome: 'authenticated';
  email: string;
  role: UserRole;
  mustChangePassword: boolean;
}
export interface MeUnauthenticated {
  outcome: 'unauthenticated';
}
export interface MeNetworkError {
  outcome: 'network-error';
}
export type MeResult = MeAuthenticated | MeUnauthenticated | MeNetworkError;

export type ChangePasswordOutcome =
  | { outcome: 'success' }
  | { outcome: 'rejected' }
  | { outcome: 'network-error' };

export type CompletePasswordSetupOutcome =
  | { outcome: 'success' }
  | { outcome: 'rejected' }
  | { outcome: 'network-error' };

/** The password is used here and nowhere else in the frontend. */
export async function apiLogin(email: string, password: string): Promise<LoginResult> {
  let response: Response;
  try {
    response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) {
    const data = (await response.json()) as { email: string };
    return { outcome: 'success', email: data.email };
  }
  if (response.status === 403) {
    // Both 403 reasons are deliberate, documented exceptions to login.ts's
    // otherwise-generic failures. Read the machine-readable `reason` rather
    // than matching the human-facing error text, which is display copy and
    // free to change. An unrecognised/absent reason falls back to
    // pending-approval — the pre-existing behaviour for every 403 before
    // this addendum, so an older server can never make this branch wrong.
    const data = (await response.json().catch(() => ({}))) as { reason?: string };
    return data.reason === 'unverified'
      ? { outcome: 'email-unverified' }
      : { outcome: 'pending-approval' };
  }
  return { outcome: 'invalid-credentials' };
}

export type ResendVerificationOutcome = 'sent' | 'network-error';

/**
 * Requests a fresh verification email. The server's response is deliberately
 * identical whether the account exists, is already verified, or is inside
 * its resend cooldown (non-enumeration — see resend-verification.ts), so
 * there is exactly one success outcome here to surface.
 */
export async function apiResendVerification(email: string): Promise<ResendVerificationOutcome> {
  try {
    await fetch('/api/auth/resend-verification', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    return 'network-error';
  }
  return 'sent';
}

export type VerifyEmailOutcome = 'verified' | 'invalid' | 'expired' | 'used' | 'network-error';

/**
 * Redeems a verification token. The token comes from the URL the user
 * clicked, and is sent to the server as-is — the server's token → user
 * lookup is the sole authority for which account gets verified (§16). No
 * client-side identity is ever consulted or transmitted here.
 */
export async function apiVerifyEmail(token: string): Promise<VerifyEmailOutcome> {
  let response: Response;
  try {
    response = await fetch('/api/auth/verify-email', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    return 'network-error';
  }

  const data = (await response.json().catch(() => ({}))) as { outcome?: string };
  if (response.status === 200 && data.outcome === 'verified') return 'verified';
  if (data.outcome === 'expired') return 'expired';
  if (data.outcome === 'used') return 'used';
  return 'invalid';
}

export type RegisterResult =
  | { outcome: 'success'; email: string }
  | { outcome: 'invalid' }
  | { outcome: 'email-taken' }
  | { outcome: 'network-error' };

/**
 * Self-service registration (Registration + Approval plan). Never
 * authenticates — the server creates the account as 'pending' and issues no
 * cookie; the caller is responsible for showing the "awaiting approval"
 * message and routing back to Login, not for treating this as a sign-in.
 */
export async function apiRegister(
  email: string,
  password: string,
  confirmPassword: string,
): Promise<RegisterResult> {
  let response: Response;
  try {
    response = await fetch('/api/auth/register', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password, confirmPassword }),
    });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 201) {
    const data = (await response.json()) as { email: string };
    return { outcome: 'success', email: data.email };
  }
  if (response.status === 409) return { outcome: 'email-taken' };
  return { outcome: 'invalid' };
}

/**
 * Best-effort. A failure here is expected and handled by the caller (retry on
 * next reconnect) — it never blocks the local logout effect.
 */
export async function apiLogout(): Promise<LogoutOutcome> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    return 'revoked';
  } catch {
    return 'network-error';
  }
}

/** The only authoritative authentication check — AUTH-ARCHITECTURE.md §11. */
export async function apiMe(): Promise<MeResult> {
  let response: Response;
  try {
    response = await fetch('/api/auth/me', { method: 'GET', credentials: 'include' });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) {
    const data = (await response.json()) as { email: string; role: UserRole; mustChangePassword: boolean };
    return { outcome: 'authenticated', email: data.email, role: data.role, mustChangePassword: data.mustChangePassword };
  }
  return { outcome: 'unauthenticated' };
}

/**
 * One-time forced transition off the provisional bootstrap credential
 * (Master Phase 11 §6). No current-password field — a valid session is
 * itself the proof of identity for this specific, narrow transition.
 */
export async function apiCompletePasswordSetup(newPassword: string): Promise<CompletePasswordSetupOutcome> {
  let response: Response;
  try {
    response = await fetch('/api/auth/complete-password-setup', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ newPassword }),
    });
  } catch {
    return { outcome: 'network-error' };
  }
  return response.status === 200 ? { outcome: 'success' } : { outcome: 'rejected' };
}

/** General-purpose voluntary password change — requires the current password. */
export async function apiChangePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordOutcome> {
  let response: Response;
  try {
    response = await fetch('/api/auth/change-password', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  } catch {
    return { outcome: 'network-error' };
  }
  return response.status === 200 ? { outcome: 'success' } : { outcome: 'rejected' };
}
